import React, { useEffect, useState } from 'react';
import { Route } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames';
import {
	faChartBar,
	faExclamationCircle,
	faClipboardCheck,
	faClipboard,
	faClipboardList,
	faCartPlus,
	faShoppingCart,
	faShoppingBasket,
	faTag,
} from '@fortawesome/free-solid-svg-icons';

import { useAuth } from '../contexts/auth';

import LoadingScreen from '../components/LoadingScreen';
import AppTopbar from '../components/PageHeader/PageHeader';
import AppMenu from '../components/SideMenu';
import AppProfile from '../components/ProfileField';

import PageFooter from 'src/components/PageFooter/PageFooter';
import ReceivedDemandsList from 'src/pages/Demands/ReceivedDemandsList';
import OrderServiceList from 'src/pages/Mecanico/OrderServiceList';
import FinishOrderServiceList from 'src/pages/Mecanico/FinishOrderServiceList';
import OrderServiceForm from 'src/pages/Mecanico/OrderServiceForm';
import OrderServiceEditForm from 'src/pages/Mecanico/OrderServiceEditForm';
import GarageDashboard from 'src/pages/Dashboard/Garage/GarageDashboard';
import ProductBrandsList from 'src/pages/ProductBrands/ProductBrandsList';
import ProductBrandForm from 'src/pages/ProductBrands/ProductBrandsForm';
import ProductGroupsList from 'src/pages/ProductGroup/ProductGroupsList';
import ProductGroupForm from 'src/pages/ProductGroup/ProductGroupsForm';
import ProductSubgroupsList from 'src/pages/ProductSubgroup/ProductSubgroupsList';
import ProductSubgroupForm from 'src/pages/ProductSubgroup/ProductSubgroupsForm';
import ProductsList from 'src/pages/Products/ProductsList';
import ProductForm from 'src/pages/Products/ProductForm';
import { faEdit } from '@fortawesome/free-regular-svg-icons';
import CreateOrderServiceNewForm from 'src/pages/Mecanico/OrderServiceNewForm';
import DemandForm from 'src/pages/Demands/DemandForm';

const MechanicalRoutes: React.FC = () => {
	const [layoutMode] = useState('static');
	const [staticMenuInactive, setStaticMenuInactive] = useState(false);
	const [overlayMenuActive, setOverlayMenuActive] = useState(false);
	const [mobileMenuActive, setMobileMenuActive] = useState(false);
	const [menuClick, setMenuClick] = useState(false);

	const addClass = (element: HTMLElement, className: string) => {
		if (element.classList) element.classList.add(className);
		else element.className += ' ' + className;
	};

	const removeClass = (element: HTMLElement, className: string) => {
		if (element.classList) element.classList.remove(className);
		else
			element.className = element.className.replace(
				new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'),
				' ',
			);
	};

	useEffect(() => {
		if (mobileMenuActive) addClass(document.body, 'body-overflow-hidden');
		else removeClass(document.body, 'body-overflow-hidden');
		if (isDesktop()) {
			setMobileMenuActive(false);
		} else {
			setStaticMenuInactive(false);
		}
	}, [mobileMenuActive]);

	function isDesktop() {
		return window.innerWidth > 1024;
	}

	function onWrapperClick() {
		if (!menuClick) {
			setOverlayMenuActive(false);
		}
		setMenuClick(false);
	}

	function onToggleMenu() {
		if (isDesktop()) {
			if (layoutMode === 'overlay') {
				setOverlayMenuActive(!overlayMenuActive);
			} else if (layoutMode === 'static') {
				setStaticMenuInactive(!staticMenuInactive);
			}
		} else {
			setMobileMenuActive(!mobileMenuActive);
		}
	}

	function onSidebarClick() {
		setMenuClick(true);
	}

	function onMenuItemClick(event: any) {
		if (!event.item.menuItems) {
			setOverlayMenuActive(false);
			setMobileMenuActive(false);
		}
	}

	function isSidebarVisible() {
		if (isDesktop()) {
			if (layoutMode === 'static') {
				return !staticMenuInactive;
			} else if (layoutMode === 'overlay') return overlayMenuActive;
			else return !staticMenuInactive;
		} else {
			return true;
		}
	}

	const { loading } = useAuth();

	if (loading) {
		return <LoadingScreen />;
	}

	const menu = [
		{ label: 'Dashboard', icon: faChartBar, to: '/dashboard' },
		{ label: 'Demandas', icon: faExclamationCircle, to: '/listReceivedDemands' },
		{
			label: 'Ordens de Serviço',
			icon: faClipboardList,
			menuItem: [
				{ label: 'Criar nova', to: 'newOrderServiceForm', icon: faEdit },
				{ label: 'Em andamento', to: 'orderServiceList', icon: faClipboard },
				{ label: 'Finalizadas', to: 'finishOrderServiceList', icon: faClipboardCheck },
			],
		},
		{
			label: 'Produtos',
			icon: faCartPlus,
			menuItem: [
				{ label: 'Produtos', icon: faShoppingCart, to: '/listProducts' },
				{ label: 'Grupo de Produtos', icon: faShoppingBasket, to: '/listProductGroups' },
				{ label: 'Subgrupo de Produtos', icon: faShoppingBasket, to: '/listProductSubgroups' },
				{ label: 'Marca de Produtos', icon: faTag, to: '/listProductBrands' },
			],
		},
	];

	const wrapperClass = classNames('layout-wrapper', {
		'layout-overlay': layoutMode === 'overlay',
		'layout-static': layoutMode === 'static',
		'layout-static-sidebar-inactive': staticMenuInactive && layoutMode === 'static',
		'layout-overlay-sidebar-active': overlayMenuActive && layoutMode === 'overlay',
		'layout-mobile-sidebar-active': mobileMenuActive,
	});

	return (
		<div className={wrapperClass} onClick={onWrapperClick}>
			<AppTopbar onToggleMenu={onToggleMenu} />

			<CSSTransition
				classNames='layout-sidebar'
				timeout={{ enter: 200, exit: 200 }}
				in={isSidebarVisible()}
				unmountOnExit>
				<div className='layout-sidebar layout-sidebar-dark' onClick={onSidebarClick}>
					<AppProfile />
					<AppMenu model={menu} onMenuItemClick={onMenuItemClick} />
				</div>
			</CSSTransition>

			<div className='layout-main'>
				<Route path='/dashboard' exact component={GarageDashboard} />
				<Route path='/listReceivedDemands' exact component={ReceivedDemandsList} />
				<Route path='/orderServiceForm' exact component={OrderServiceForm} />
				<Route path='/newOrderServiceForm' exact component={CreateOrderServiceNewForm} />
				<Route path='/orderServiceEditForm' exact component={OrderServiceEditForm} />
				<Route path='/orderServiceList' exact component={OrderServiceList} />
				<Route path='/finishOrderServiceList' exact component={FinishOrderServiceList} />
				<Route path='/listProductBrands' exact component={ProductBrandsList} />
				<Route path='/productBrandForm' exact component={ProductBrandForm} />
				<Route path='/listProductGroups' exact component={ProductGroupsList} />
				<Route path='/productGroupForm' exact component={ProductGroupForm} />
				<Route path='/listProductSubgroups' exact component={ProductSubgroupsList} />
				<Route path='/productSubgroupForm' exact component={ProductSubgroupForm} />
				<Route path='/listProducts' exact component={ProductsList} />
				<Route path='/productForm' exact component={ProductForm} />
				<Route path='/DemandForm' exact component={DemandForm} />

				<PageFooter />
			</div>
			<div className='layout-mask'></div>
		</div>
	);
};

export default MechanicalRoutes;
